import { type FormEvent, useEffect, useState } from "react";
import {
  ApiError,
  api,
  type SmtpConfigIn,
  type SmtpConfigOut,
  type SmtpEncryption,
  type SmtpTestOut,
} from "@/lib/api";
import { canEdit, useAuth } from "@/lib/auth";
import NotificacionesPanel from "@/components/perfil/NotificacionesPanel";
import TelegramPanel from "@/components/perfil/TelegramPanel";
import RichTextEditor from "@/components/RichTextEditor";
import ConfirmDialog from "@/ui/ConfirmDialog";
import AyudaVista from "@/ui/AyudaVista";
import { ToggleTrack } from "@/ui/Toggle";
import { ayudaPerfil } from "@/components/ayuda/vistas/perfil";
import { useToast } from "@/ui/Toast";
import BuzonPanel from "@/components/perfil/BuzonPanel";

const DEFAULTS = {
  smtp_host: "mail.emprenet.com.mx",
  smtp_port: 465,
  smtp_encryption: "ssl" as SmtpEncryption,
};

/**
 * Perfil del user logueado. Por ahora solo gestiona el correo empresarial
 * SMTP (Fase B). Cada user de soporte da de alta SU correo (ej.
 * soporte@emprenet.com.mx) y desk lo usa para enviar correos en su nombre
 * (Fase C integra al pipeline real). El password se guarda encriptado con
 * Fernet en backend.
 */
export default function Perfil() {
  const me = useAuth((s) => s.user);
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<SmtpConfigOut | null>(null);

  const [smtpEmail, setSmtpEmail] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpHost, setSmtpHost] = useState(DEFAULTS.smtp_host);
  const [smtpPort, setSmtpPort] = useState<number>(DEFAULTS.smtp_port);
  const [smtpEncryption, setSmtpEncryption] = useState<SmtpEncryption>(
    DEFAULTS.smtp_encryption,
  );
  const [smtpCcoSelf, setSmtpCcoSelf] = useState(true);
  const [smtpFromName, setSmtpFromName] = useState("");
  const [signatureHtml, setSignatureHtml] = useState("");

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [testResult, setTestResult] = useState<SmtpTestOut | null>(null);

  async function load() {
    setLoading(true);
    try {
      const c = await api.get<SmtpConfigOut>("/api/users/me/smtp");
      setConfig(c);
      // Pre-fill con lo que ya esté guardado
      setSmtpEmail(c.smtp_email || me?.email || "");
      setSmtpHost(c.smtp_host || DEFAULTS.smtp_host);
      setSmtpPort(c.smtp_port || DEFAULTS.smtp_port);
      setSmtpEncryption((c.smtp_encryption as SmtpEncryption) || DEFAULTS.smtp_encryption);
      setSmtpCcoSelf(c.smtp_cco_self);
      setSmtpFromName(c.smtp_from_name || "");
      setSignatureHtml(c.smtp_signature_html || "");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar tu configuración", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildPayload(): SmtpConfigIn {
    return {
      smtp_email: smtpEmail.trim(),
      smtp_password: smtpPassword,
      smtp_host: smtpHost.trim(),
      smtp_port: smtpPort,
      smtp_encryption: smtpEncryption,
      smtp_signature_html: signatureHtml.trim() || null,
      smtp_cco_self: smtpCcoSelf,
      smtp_from_name: smtpFromName.trim() || null,
    };
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!smtpPassword.trim()) {
      toast.error(
        "Contraseña requerida",
        "Vuelve a escribir tu contraseña para guardar (cualquier cambio requiere volver a autenticar).",
      );
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const updated = await api.put<SmtpConfigOut>("/api/users/me/smtp", buildPayload());
      setConfig(updated);
      setSmtpPassword(""); // limpiar password del form
      toast.success(
        "Configuración guardada",
        'Recomiendo darle a "Probar conexión" para confirmar las credenciales.',
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude guardar", msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      // Si el user tiene la config guardada Y no esta editando el password,
      // testeamos contra la guardada (manda correo de prueba al propio user).
      // Si esta editando (escribio password nuevo), testeamos con esos campos.
      const usingForm = !!smtpPassword.trim();
      const payload = usingForm
        ? {
            smtp_email: smtpEmail.trim(),
            smtp_password: smtpPassword,
            smtp_host: smtpHost.trim(),
            smtp_port: smtpPort,
            smtp_encryption: smtpEncryption,
          }
        : {};
      const r = await api.post<SmtpTestOut>("/api/users/me/smtp/test", payload);
      setTestResult(r);
      if (r.ok) {
        toast.success("Conexión correcta", r.detalle);
        if (!usingForm) await load(); // refresca smtp_verified_at
      } else {
        toast.error("La prueba falló", r.detalle);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("Error en la prueba", msg);
    } finally {
      setTesting(false);
    }
  }

  async function handleClear() {
    try {
      await api.del("/api/users/me/smtp");
      toast.success("Configuración de correo borrada");
      setSmtpPassword("");
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude limpiar", msg);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">Mi perfil</h1>
            {/* Una sola Ayuda para toda la página, con una sección por bloque
                (correo, buzón, notificaciones, equipos, Telegram). */}
            <AyudaVista titulo="Mi perfil" {...ayudaPerfil({ puedeEnviar: canEdit(me) })} />
          </div>
          <p className="text-sm text-zinc-400 break-words">
            {me?.nombre} · {me?.email} · {me?.rol}
          </p>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <h2 className="text-sm font-medium text-zinc-300">Mi correo empresarial</h2>
            <p className="text-xs text-zinc-500 mt-1">
              Configura tu cuenta de correo empresarial real (ej.{" "}
              <code>soporte@emprenet.com.mx</code>) para que el sistema envíe en tu
              nombre. El cliente recibe el correo del alias empresarial real,
              manteniendo el diseño de la empresa intacto. Se usa para enviar (y, si
              lo prendes abajo, para «Revisar mi buzón» sin tocar nada) — los
              correos entrantes del equipo se manejan por separado en{" "}
              <a href="/bandeja" className="text-accent-400 underline">
                /bandeja
              </a>
              .
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500">Cargando configuración...</p>
        ) : (
          <>
            {/* Status banner */}
            <StatusBanner config={config} testResult={testResult} />

            <form onSubmit={handleSave} className="space-y-4">
              {/* Email + Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">
                    Correo empresarial
                  </label>
                  <input
                    type="email"
                    value={smtpEmail}
                    onChange={(e) => setSmtpEmail(e.target.value)}
                    placeholder="soporte@emprenet.com.mx"
                    required
                    className="input w-full"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">
                    Contraseña{" "}
                    {config?.has_password && (
                      <span className="text-zinc-600">
                        (ya configurado — re-escribe para guardar)
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder={
                      config?.has_password ? "•••••••• (configurado)" : "tu contraseña"
                    }
                    autoComplete="new-password"
                    className="input w-full"
                  />
                </div>
              </div>

              {/* Nombre de salida (override del From:) */}
              <div>
                <label className="text-xs text-zinc-400 block mb-1">
                  Nombre de salida (opcional)
                </label>
                <input
                  type="text"
                  value={smtpFromName}
                  onChange={(e) => setSmtpFromName(e.target.value)}
                  placeholder={`Vacío = "${me?.nombre || "tu nombre"}" (predeterminado)`}
                  maxLength={120}
                  className="input w-full"
                />
                <p className="mt-1 text-[11px] text-zinc-500">
                  El nombre que ve el cliente como remitente (ej. «Soporte Emprenet»). Vacío =
                  tu nombre del perfil. No cambia tu nombre en la bitácora, solo en el correo.
                </p>
              </div>

              {/* Host + Port + Encryption */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">Servidor de correo</label>
                  <input
                    type="text"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="mail.emprenet.com.mx"
                    required
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">Puerto</label>
                  <input
                    type="number"
                    min={1}
                    max={65535}
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(parseInt(e.target.value, 10) || 0)}
                    required
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">Cifrado</label>
                  <select
                    value={smtpEncryption}
                    onChange={(e) => setSmtpEncryption(e.target.value as SmtpEncryption)}
                    className="input w-full"
                  >
                    <option value="ssl">SSL (port 465)</option>
                    <option value="starttls">STARTTLS (port 587)</option>
                    <option value="none">Sin cifrado (no recomendado)</option>
                  </select>
                </div>
              </div>

              {/* Toggle CCO */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={smtpCcoSelf}
                  onChange={(e) => setSmtpCcoSelf(e.target.checked)}
                />
                <ToggleTrack checked={smtpCcoSelf} className="mt-1" />
                <div>
                  <p className="text-sm text-zinc-200">
                    Mandarme copia oculta de cada correo
                  </p>
                  <p className="text-xs text-zinc-500">
                    Cada correo que mandes desde el sistema te llegará también a tu
                    bandeja personal del correo empresarial.
                  </p>
                </div>
              </label>

              {/* Firma HTML */}
              <div>
                <label className="text-xs text-zinc-400 block mb-1">
                  Firma
                </label>
                <RichTextEditor
                  value={signatureHtml}
                  onChange={(html) => setSignatureHtml(html)}
                  placeholder="Tu nombre, puesto, teléfono, enlace a sitio... (opcional)"
                  minHeight={140}
                />
                {/* Solo ahí: la plantilla del cliente ya trae el pie de la empresa
                    (api/correos.py, `inyectar_firma` solo si destinatario == "jimi"). */}
                <p className="mt-1 text-[11px] text-zinc-500">
                  Va al final de los correos que mandas al proveedor (salvo al responder
                  citando otro correo). Los del cliente llevan el pie de la empresa.
                </p>
              </div>

              {/* Botones */}
              <div className="flex flex-col gap-2 pt-2 border-t border-ink-700 sm:flex-row sm:items-center sm:flex-wrap">
                <button
                  type="submit"
                  className="btn-primary text-sm w-full sm:w-auto"
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Guardar configuración"}
                </button>
                <button
                  type="button"
                  onClick={handleTest}
                  className="btn-ghost text-sm w-full sm:w-auto"
                  disabled={testing || (!config?.has_password && !smtpPassword.trim())}
                >
                  {testing ? "Probando..." : "Probar conexión"}
                </button>
                {config?.has_password && (
                  <button
                    type="button"
                    onClick={() => setConfirmClear(true)}
                    className="btn-ghost text-sm text-red-400 hover:text-red-300 w-full sm:w-auto sm:ml-auto"
                  >
                    Limpiar configuración
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </div>

      <BuzonPanel />

      <NotificacionesPanel />

      <TelegramPanel />

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title="Limpiar configuración de correo"
        description="Se borra tu correo empresarial, contraseña, servidor, puerto, firma — todo. Tendrás que volver a darlo de alta para mandar correos. ¿Confirmas?"
        confirmLabel="Limpiar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleClear}
      />
    </div>
  );
}

// ============================================================================
// Status banner
// ============================================================================

function StatusBanner({
  config,
  testResult,
}: {
  config: SmtpConfigOut | null;
  testResult: SmtpTestOut | null;
}) {
  if (testResult && !testResult.ok) {
    return (
      <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
        ❌ La prueba falló: {testResult.detalle}
      </div>
    );
  }
  if (testResult && testResult.ok) {
    return (
      <div className="rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
        ✅ {testResult.detalle}
      </div>
    );
  }
  if (!config?.has_password) {
    return (
      <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
        ⚠ No tienes tu correo configurado todavía. Cuando intentes enviar un correo
        desde un caso, el botón "Enviar" estará bloqueado hasta que lo configures
        aquí.
      </div>
    );
  }
  if (config.smtp_verified_at) {
    return (
      <div className="rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
        ✅ Verificado el {new Date(config.smtp_verified_at).toLocaleString()}
      </div>
    );
  }
  return (
    <div className="rounded border border-zinc-500/30 bg-zinc-500/10 px-3 py-2 text-sm text-zinc-300">
      Configuración guardada pero aún NO verificada. Dale a "Probar conexión".
    </div>
  );
}
